import { MoreHorizontal } from 'lucide-react'
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react'
import { cn } from '@/lib/cn'

export interface RowMenuItem {
  id: string
  label: string
  onSelect: () => void
  /** a destructive action (refund, cancel with refund, switch off): danger text. The caller
   *  still asks first through `Confirm` — the menu only keeps it one deliberate step away. */
  danger?: boolean
  disabled?: boolean
}

/** Where the open menu sits: fixed to the viewport (a table's own scroll box would clip an
 *  absolutely placed popover), under the button or above it near the bottom edge. */
interface Place {
  top: number
  right: number
  up: boolean
}

const MENU_GAP = 4
const MENU_MAX_H = 240

/**
 * The «⋯» menu of a table row (WP-17, design §6.6): destructive row actions («Вернуть»,
 * «Отменить с возвратом», «Отключить») live here or in the row's drawer, never as a red button
 * one stray click away in every row. A `ghost` icon button 32 px (44 px on a touch screen)
 * named by `label` («Действия с подарком #42»); the menu is e2 (`surface-2`, hairline-strong,
 * shadow), `role="menu"` with `menuitem`s: ↑/↓, Home/End move, Enter/Space choose, Esc and a
 * click outside close it and return focus to the button. Clicks inside never reach the row
 * (a row click opens its drawer).
 */
export function RowMenu({
  label,
  items,
  defaultOpen = false,
}: {
  /** what the button does, with the row in it: «Действия с платежом 3f2a9c1e» */
  label: string
  items: readonly RowMenuItem[]
  /** stories: start open */
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  const [place, setPlace] = useState<Place | null>(null)
  const button = useRef<HTMLButtonElement>(null)
  const menu = useRef<HTMLDivElement>(null)
  const id = useId()

  const measure = useCallback(() => {
    const el = button.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const up = window.innerHeight - r.bottom < MENU_MAX_H && r.top > MENU_MAX_H
    setPlace({ top: up ? r.top - MENU_GAP : r.bottom + MENU_GAP, right: Math.max(8, window.innerWidth - r.right), up })
  }, [])

  const close = useCallback((refocus: boolean) => {
    setOpen(false)
    if (refocus) button.current?.focus()
  }, [])

  useLayoutEffect(() => {
    if (open) measure()
  }, [open, measure])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (!menu.current?.contains(t) && !button.current?.contains(t)) close(false)
    }
    // The menu is fixed to the viewport: it follows nothing, so a scroll or a resize closes it.
    const onMove = () => close(false)
    document.addEventListener('mousedown', onDown)
    window.addEventListener('resize', onMove)
    window.addEventListener('scroll', onMove, true)
    return () => {
      document.removeEventListener('mousedown', onDown)
      window.removeEventListener('resize', onMove)
      window.removeEventListener('scroll', onMove, true)
    }
  }, [open, close])

  useEffect(() => {
    if (open && place && !defaultOpen) menu.current?.querySelector<HTMLElement>('[role="menuitem"]:not([disabled])')?.focus()
  }, [open, place, defaultOpen])

  function onMenuKey(e: KeyboardEvent<HTMLDivElement>) {
    const list = [...(menu.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])') ?? [])]
    const at = list.indexOf(document.activeElement as HTMLElement)
    let next = -1
    if (e.key === 'ArrowDown') next = (at + 1) % list.length
    else if (e.key === 'ArrowUp') next = (at - 1 + list.length) % list.length
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = list.length - 1
    else if (e.key === 'Escape' || e.key === 'Tab') {
      e.preventDefault()
      close(true)
      return
    }
    if (next < 0) return
    e.preventDefault()
    list[next]?.focus()
  }

  if (items.length === 0) return null
  return (
    // A row click opens the row's drawer: nothing in here bubbles up to it.
    <span className="inline-flex print:hidden" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <button
        ref={button}
        type="button"
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && !open) {
            e.preventDefault()
            setOpen(true)
          }
        }}
        className={cn(
          'inline-flex size-8 items-center justify-center rounded-sm text-ink-2 transition-colors duration-micro',
          'hover:bg-surface-3 hover:text-ink-1 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan',
          '[@media(pointer:coarse)]:size-11',
          open && 'bg-surface-3 text-ink-1',
        )}
      >
        <MoreHorizontal aria-hidden size={16} strokeWidth={1.75} />
      </button>
      {open && place && (
        <div
          ref={menu}
          id={id}
          role="menu"
          aria-label={label}
          onKeyDown={onMenuKey}
          style={{ top: place.top, right: place.right }}
          className={cn(
            'fixed z-[60] flex min-w-48 max-w-[calc(100vw-2rem)] flex-col overflow-y-auto rounded-sm border border-hairline-strong bg-surface-2 p-1 shadow-e2',
            'max-h-60',
            place.up && '-translate-y-full',
          )}
        >
          {items.map((it) => (
            <button
              key={it.id}
              type="button"
              role="menuitem"
              disabled={it.disabled}
              onClick={() => {
                close(true)
                it.onSelect()
              }}
              className={cn(
                'flex min-h-9 items-center whitespace-nowrap rounded-xs px-3 text-left text-body',
                'hover:bg-surface-3 focus-visible:bg-surface-3 focus-visible:outline-none [@media(pointer:coarse)]:min-h-11',
                'disabled:pointer-events-none disabled:text-ink-3',
                it.danger ? 'text-danger' : 'text-ink-1',
              )}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </span>
  )
}
